import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLang } from "@/context/LanguageContext";
import { Camera, X, Keyboard } from "lucide-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType, NotFoundException } from "@zxing/library";

const HINTS = new Map();
HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.CODE_128,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.ITF,
]);
HINTS.set(DecodeHintType.TRY_HARDER, true);

// Native, hardware-accelerated formats (Chrome/Edge/Android). When supported this
// is dramatically faster and rotation-invariant compared to the pure-JS ZXing fallback.
const NATIVE_FORMATS = [
  "qr_code", "ean_13", "ean_8", "upc_a", "upc_e",
  "code_128", "code_39", "code_93", "itf", "data_matrix", "pdf417", "aztec",
];

export default function BarcodeScanner({ onScan, onClose }) {
  const { t } = useLang();
  // videoRef is the raw decode source (kept off-screen); some browsers render
  // hardware-composited <video> as black, so we mirror its frames onto a
  // visible <canvas> instead, which always paints as a normal bitmap.
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const controlsRef = useRef(null);
  const detectorTimerRef = useRef(null);
  const scannedRef = useRef(false);
  const cancelledRef = useRef(false);
  const watchdogRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const stalledSinceRef = useRef(0);
  const restartAttemptsRef = useRef(0);
  const MAX_RESTART_ATTEMPTS = 3;
  const [hasCamera, setHasCamera] = useState(false);
  const [manual, setManual] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    cancelledRef.current = false;
    startCamera();
    return () => {
      cancelledRef.current = true;
      stopCamera();
    };
  }, []);

  const startDrawLoop = () => {
    const draw = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState >= 2 && video.videoWidth) {
        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
  };

  // Some browsers occasionally attach a camera stream that never actually
  // produces advancing frames (stuck/black preview) right after permission
  // is granted. Detect a stalled video and transparently restart the stream,
  // mirroring the manual close-and-reopen that reliably fixes it for users.
  const startWatchdog = () => {
    stopWatchdog();
    lastFrameTimeRef.current = -1;
    stalledSinceRef.current = Date.now();
    watchdogRef.current = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      const currentTime = video.currentTime;
      if (currentTime > lastFrameTimeRef.current) {
        lastFrameTimeRef.current = currentTime;
        stalledSinceRef.current = Date.now();
        restartAttemptsRef.current = 0;
        return;
      }
      const stalledFor = Date.now() - stalledSinceRef.current;
      if (stalledFor > 2000) {
        if (restartAttemptsRef.current < MAX_RESTART_ATTEMPTS) {
          restartAttemptsRef.current += 1;
          stalledSinceRef.current = Date.now();
          restartCamera();
        } else {
          stopWatchdog();
          setCameraError(t("scanner.cameraStalled"));
          setShowManual(true);
          stopCamera();
        }
      }
    }, 800);
  };

  const stopWatchdog = () => {
    if (watchdogRef.current) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
  };

  const restartCamera = () => {
    teardownDecoders();
    setHasCamera(false);
    startCamera();
  };

  // Checks for the native Shape Detection API and returns the subset of
  // formats we care about that the browser can actually detect in hardware.
  const getNativeFormats = async () => {
    if (typeof window === "undefined" || !("BarcodeDetector" in window)) return null;
    try {
      const supported = await window.BarcodeDetector.getSupportedFormats();
      const usable = NATIVE_FORMATS.filter((f) => supported.includes(f));
      return usable.length ? usable : null;
    } catch {
      return null;
    }
  };

  // Fast path: native, hardware-accelerated detection running at a high poll
  // rate. Rotation-invariant, no per-frame JS decode cost.
  const startNativeDetection = (formats) => {
    const detector = new window.BarcodeDetector({ formats });
    let busy = false;
    detectorTimerRef.current = setInterval(async () => {
      if (busy || scannedRef.current) return;
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;
      busy = true;
      try {
        const results = await detector.detect(video);
        if (results.length && !scannedRef.current) {
          scannedRef.current = true;
          stopCamera();
          onScan(results[0].rawValue);
        }
      } catch {
        // transient detect errors are expected between frames; ignore
      } finally {
        busy = false;
      }
    }, 120);
  };

  // Fallback path: pure-JS ZXing decode against the already-open stream,
  // tuned to scan far more often than the library's slow 500ms default.
  const startZxingDecode = async (stream) => {
    const reader = new BrowserMultiFormatReader(HINTS, {
      delayBetweenScanAttempts: 80,
      delayBetweenScanSuccess: 500,
    });
    const controls = await reader.decodeFromStream(stream, videoRef.current, (result, err) => {
      if (result && !scannedRef.current) {
        scannedRef.current = true;
        stopCamera();
        onScan(result.getText());
      } else if (err && !(err instanceof NotFoundException)) {
        // ignore per-frame decode misses; log unexpected errors only
        console.warn("Barcode decode error:", err);
      }
    });
    if (cancelledRef.current) {
      controls.stop();
      return;
    }
    controlsRef.current = controls;
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      if (cancelledRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await new Promise((resolve) => {
        if (video.readyState >= 1) resolve();
        else video.onloadedmetadata = resolve;
      });
      await video.play().catch(() => {});
      if (cancelledRef.current) return;

      setHasCamera(true);
      setScanning(true);
      startDrawLoop();
      startWatchdog();

      const nativeFormats = await getNativeFormats();
      if (cancelledRef.current) return;
      if (nativeFormats) {
        startNativeDetection(nativeFormats);
      } else {
        await startZxingDecode(stream);
      }
    } catch (err) {
      if (cancelledRef.current) return;
      setCameraError(t("scanner.cameraUnavailable"));
      setShowManual(true);
    }
  };

  const teardownDecoders = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (detectorTimerRef.current) {
      clearInterval(detectorTimerRef.current);
      detectorTimerRef.current = null;
    }
    controlsRef.current?.stop();
    controlsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const stopCamera = () => {
    stopWatchdog();
    teardownDecoders();
    setScanning(false);
  };

  const handleManual = (e) => {
    e.preventDefault();
    if (manual.trim()) {
      stopCamera();
      onScan(manual.trim());
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <video ref={videoRef} className="hidden" autoPlay playsInline muted />

      <div className="relative flex-1 min-h-0">
        {!cameraError ? (
          <>
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full object-cover" />
            {!hasCamera && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40">
                {t("scanner.starting")}
              </div>
            )}
            {hasCamera && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-8">
                <div className="relative w-full max-w-sm h-28 rounded-2xl">
                  <div className="absolute inset-0 border-2 border-white/80 rounded-2xl" />
                  <div className="scan-line absolute left-0 w-full h-0.5 bg-blue-400 shadow-[0_0_8px_2px_rgba(96,165,250,0.8)]"
                    style={{ animation: "scan-line 1.6s ease-in-out infinite alternate" }} />
                </div>
              </div>
            )}
            {scanning && (
              <div className="absolute bottom-6 left-0 right-0 text-center px-6">
                <span className="text-white text-sm bg-black/60 px-4 py-2 rounded-full">
                  {t("scanner.scanning")}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-center gap-2">
              <Camera className="w-4 h-4 shrink-0" />
              {cameraError}
            </div>
          </div>
        )}

        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-4 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center gap-2 text-white text-base font-medium">
            <Camera className="w-5 h-5" />
            {t("scanner.title")}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-white/90 hover:text-white p-1"
            data-testid="barcode-scanner-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="bg-white px-4 py-4 space-y-3 shrink-0">
        <button
          type="button"
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
          onClick={() => setShowManual(!showManual)}
        >
          <Keyboard className="w-4 h-4" />
          {showManual ? t("scanner.hideManual") : t("scanner.enterManually")}
        </button>

        {(showManual || !hasCamera) && (
          <form onSubmit={handleManual} className="flex gap-2">
            <Input
              value={manual}
              onChange={e => setManual(e.target.value)}
              placeholder={t("scanner.manualPlaceholder")}
              autoFocus
              className="h-12 text-base"
              data-testid="manual-barcode-input"
            />
            <Button type="submit" className="h-12 px-4 bg-blue-600 hover:bg-blue-700" data-testid="manual-barcode-submit">
              {t("scanner.ok")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
